import { bookingRefundOffers } from "../../../app/api/cancelBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { IMultiBookingRefundSummary, IMoneySummary } from "../../../Orchestrator/Recoils/MultiBookingAtom";
import { IBookingResponse } from "../../../Orchestrator/Types/Index";
import { IRefundOfferRequest } from "../../../Orchestrator/Types/Types/BookingTypes";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { sumRefundOffers } from "../../../Lib/Utils/refundMoney";

export interface IGetMultiBookingRefundSummaryParams {
  booking: IBookingResponse | null;
}

export type IGetMultiBookingRefundSummaryResult = IMultiBookingRefundSummary | null;

class GetMultiBookingRefundSummaryAction extends BaseAction<
  IGetMultiBookingRefundSummaryResult,
  IGetMultiBookingRefundSummaryParams
> {
  override async run(): Promise<IGetMultiBookingRefundSummaryResult> {
    updateRecoilState("multiBookingRefundSummaryAtom", null);
    const booking = this.params?.booking;
    const bookingPayload = booking?.booking;

    if (!bookingPayload?.id) {
      throw new Error("BOOKING_NOT_FOUND");
    }

    const fulfillmentIds = this.collectFulfillmentIds(bookingPayload) || [];

    if (fulfillmentIds.length === 0) {
      throw new Error("NO_ELIGIBLE_FULFILLMENTS");
    }

    const payload: IRefundOfferRequest = {
      fulfillmentIds,
      overruleCode: null,
      refundDate: new Date().toISOString(),
    };

    const response = await bookingRefundOffers(payload, bookingPayload.id);
    if (response.status !== "ok" || !response.answer?.refundOffers?.length) {
      throw new Error("NO_REFUND_OFFERS");
    }

    /**
     * EVERY offer, not `refundOffers[0]`.
     *
     * `collectFulfillmentIds` bundles every admission and ancillary across every
     * booked offer into one request, and the retailer answers with one refund
     * offer per booked offer — each carrying its OWN `refundFee` and
     * `refundableAmount`. Reading the first one therefore reported ONE offer's
     * figures as the whole booking's: the same defect as ACPT-023 on the cancel
     * page, on this surface.
     *
     * `sumRefundOffers` returns null rather than a number when the total cannot
     * be established (a missing amount, or offers in different currencies or
     * scales that cannot be added). Money code: that throws here, exactly as a
     * declined quote already does, instead of being coerced to AED 0.
     */
    const totals = sumRefundOffers(response.answer.refundOffers);
    if (!totals) {
      throw new Error("REFUND_OFFERS_NOT_PRICEABLE");
    }
    const summary = {
      refundableAmount: totals.refundableAmount as unknown as IMoneySummary,
      refundFee: totals.refundFee as unknown as IMoneySummary,
    };
    updateRecoilState("multiBookingRefundSummaryAtom", summary);
    return summary;
  }

  private collectFulfillmentIds(bookingPayload: IBookingResponse["booking"]) {
    const ids = new Set<string>();
    const offers = bookingPayload?.bookedOffers || [];

    offers.forEach((offer: any) => {
      (offer?.admissions || []).forEach((admission: any) => {
        if (admission?.id && admission?.status !== "REFUNDED") {
          ids.add(admission.id);
        }
      });

      (offer?.ancillaries || []).forEach((ancillary: any) => {
        if (ancillary?.id && ancillary?.status !== "REFUNDED") {
          ids.add(ancillary.id);
        }
      });
    });

    return Array.from(ids);
  }
}

export const getMultiBookingRefundSummaryAction = new GetMultiBookingRefundSummaryAction();

